// Peças de tela reaproveitadas: campos, formulário, avisos, diálogos.
import { h, icone } from '../lib/dom.js';
import { detalheDoErro, mensagemDeErro } from '../lib/erros.js';
import { FORMATOS, formatoPadrao } from '../lib/mascaras.js';
import { registrarErro } from '../lib/registro-de-erros.js';

let proximoId = 0;
const novoId = (nome) => `c-${nome}-${++proximoId}`;

// ---------------------------------------------------------------------
// Campos
// ---------------------------------------------------------------------
// tipo: text | email | tel | date | number | textarea | select | segmentos | caixa
// formato: cpf | documento | telefone | placa | km | ano | renavam | dias |
//          rntrc | dinheiro | percentual | nome | marca | email | texto
//          (ver lib/mascaras.js). Sem formato, e-mail, telefone e texto de uma
//          linha já seguem o padrão.
export function campo({
  rotulo,
  nome,
  tipo = 'text',
  valor = '',
  dica = null,
  obrigatorio = false,
  opcoes = [],
  desativado = false,
  maiusculas = false,
  formato = null,
  atributos = {},
  aoMudar = null,
}) {
  const id = novoId(nome);
  const idDica = dica ? `${id}-dica` : null;
  const idErro = `${id}-erro`;
  const descritoPor = [idDica, idErro].filter(Boolean).join(' ');

  if (tipo === 'caixa') {
    return h('div', { class: 'campo', dataset: { campo: nome } },
      h('label', { class: 'caixa' },
        h('input', { type: 'checkbox', name: nome, checked: Boolean(valor), disabled: desativado, onChange: aoMudar, 'aria-describedby': descritoPor }),
        h('span', null, rotulo)),
      dica && h('p', { class: 'dica', id: idDica }, dica),
      h('p', { class: 'erro-campo', id: idErro, role: 'alert' }));
  }

  if (tipo === 'segmentos') {
    return h('fieldset', { class: 'campo', dataset: { campo: nome }, 'aria-describedby': descritoPor },
      h('legend', null, rotulo, obrigatorio && h('span', { class: 'obrigatorio', 'aria-hidden': 'true' }, ' *')),
      h('div', { class: 'segmentos' },
        opcoes.map(([v, texto, detalhe]) =>
          h('label', { class: 'segmento' },
            h('input', { type: 'radio', name: nome, value: v, checked: String(valor) === String(v), disabled: desativado, required: obrigatorio, onChange: aoMudar }),
            h('span', null, texto, detalhe && h('small', null, detalhe))))),
      dica && h('p', { class: 'dica', id: idDica }, dica),
      h('p', { class: 'erro-campo', id: idErro, role: 'alert' }));
  }

  let controle;
  const comuns = {
    id,
    name: nome,
    required: obrigatorio,
    disabled: desativado,
    'aria-describedby': descritoPor,
    class: maiusculas ? 'maiusculas' : null,
    onInput: aoMudar,
    ...atributos,
  };
  if (tipo === 'select') {
    controle = h('select', { ...comuns, onChange: aoMudar, onInput: null }, montarOpcoes(opcoes, valor));
  } else if (tipo === 'textarea') {
    controle = h('textarea', { ...comuns, rows: 3 });
    controle.value = valor ?? '';
  } else {
    controle = h('input', { ...comuns, type: tipo, value: valor ?? '' });
    aplicarFormato(controle, formato ?? formatoPadrao(tipo));
  }

  return h('div', { class: 'campo', dataset: { campo: nome } },
    h('label', { for: id }, rotulo, obrigatorio && h('span', { class: 'obrigatorio', 'aria-hidden': 'true' }, ' *')),
    controle,
    dica && h('p', { class: 'dica', id: idDica }, dica),
    h('p', { class: 'erro-campo', id: idErro, role: 'alert' }));
}

// Opções de um select: [valor, texto] ou { grupo: 'Título', opcoes: [[valor, texto], …] }.
function montarOpcoes(opcoes, valor) {
  const opcao = ([v, texto]) => h('option', { value: v, selected: String(valor ?? '') === String(v) }, texto);
  return opcoes.map((o) => (Array.isArray(o) ? opcao(o) : h('optgroup', { label: o.grupo }, o.opcoes.map(opcao))));
}

// Troca as opções de um select já na tela, escolhendo `valor` (se existir).
export function trocarOpcoes(select, opcoes, valor = select.value) {
  select.replaceChildren(...montarOpcoes(opcoes, valor));
  if ([...select.options].some((o) => o.value === String(valor ?? ''))) select.value = String(valor ?? '');
}

// Máscara enquanto digita e padrão ao sair do campo. O cursor fica depois do
// mesmo número/letra em que estava, mesmo quando a máscara põe ou tira pontos.
const SIGNIFICATIVO = /[0-9A-Za-z]/;

function contarSignificativos(texto) {
  let n = 0;
  for (const letra of texto) if (SIGNIFICATIVO.test(letra)) n += 1;
  return n;
}

function posicaoDepoisDe(texto, quantos) {
  if (quantos <= 0) return 0;
  let vistos = 0;
  for (let i = 0; i < texto.length; i++) {
    if (SIGNIFICATIVO.test(texto[i]) && ++vistos === quantos) return i + 1;
  }
  return texto.length;
}

function mascarar(el, mascara, texto, antesDoCursor) {
  const novo = mascara(texto);
  el.value = novo;
  if (document.activeElement !== el) return;
  const pos = posicaoDepoisDe(novo, antesDoCursor);
  try {
    el.setSelectionRange(pos, pos);
  } catch {
    /* campo sem cursor */
  }
}

function aplicarFormato(el, nomeFormato) {
  const formato = FORMATOS[nomeFormato];
  if (!formato) return;
  el.dataset.formato = nomeFormato;
  if (formato.digitando) {
    if (el.value) el.value = formato.digitando(el.value);
    // Apagar um ponto ou traço apaga o número antes dele (senão a máscara
    // devolveria o ponto e o cursor ficaria preso).
    el.addEventListener('beforeinput', (evento) => {
      if (evento.inputType !== 'deleteContentBackward') return;
      const { selectionStart: inicio, selectionEnd: fim, value } = el;
      if (inicio === null || inicio !== fim || inicio === 0 || SIGNIFICATIVO.test(value[inicio - 1])) return;
      let i = inicio - 1;
      while (i > 0 && !SIGNIFICATIVO.test(value[i - 1])) i -= 1;
      if (i === 0) return;
      evento.preventDefault();
      mascarar(el, formato.digitando, value.slice(0, i - 1) + value.slice(inicio), contarSignificativos(value.slice(0, i - 1)));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    });
    el.addEventListener('input', () => {
      const { value } = el;
      if (formato.digitando(value) === value) return;
      const cursor = el.selectionStart ?? value.length;
      mascarar(el, formato.digitando, value, contarSignificativos(value.slice(0, cursor)));
    });
  }
  if (formato.final) {
    el.addEventListener('blur', () => {
      const novo = formato.final(el.value);
      if (novo !== el.value) el.value = novo;
    });
  }
}

// Valor do campo já no padrão (o mesmo que aparece ao sair dele).
export function valorPadronizado(el) {
  const final = FORMATOS[el.dataset?.formato]?.final;
  return final ? final(el.value) : el.value.trim();
}

// Lê o formulário como objeto. Caixas viram true/false; o resto vem como
// texto, já no padrão de cada campo.
export function lerDados(form) {
  const dados = {};
  for (const el of form.elements) {
    if (!el.name || el.disabled && el.type !== 'radio') continue;
    if (el.type === 'checkbox') dados[el.name] = el.checked;
    else if (el.type === 'radio') {
      if (el.checked) dados[el.name] = el.value;
      else if (!(el.name in dados)) dados[el.name] = '';
    } else dados[el.name] = valorPadronizado(el);
  }
  return dados;
}

export function limparErros(form) {
  for (const p of form.querySelectorAll('.erro-campo')) p.textContent = '';
  for (const el of form.querySelectorAll('[aria-invalid]')) el.removeAttribute('aria-invalid');
  const geral = form.querySelector('.erro-geral');
  if (geral) geral.replaceChildren();
}

// erros: { nomeDoCampo: 'mensagem' }. Foca o primeiro campo com problema.
export function mostrarErros(form, erros) {
  let primeiro = null;
  for (const [nome, mensagem] of Object.entries(erros)) {
    const bloco = form.querySelector(`[data-campo="${nome}"]`);
    if (!bloco) continue;
    bloco.querySelector('.erro-campo').textContent = mensagem;
    const controles = bloco.querySelectorAll('input, select, textarea');
    for (const c of controles) c.setAttribute('aria-invalid', 'true');
    primeiro ??= controles[0];
  }
  primeiro?.focus();
  return Object.keys(erros).length > 0;
}

export function mostrarErroGeral(form, erro, { comDetalhe = false } = {}) {
  const geral = form.querySelector('.erro-geral');
  const texto = typeof erro === 'string' ? erro : mensagemDeErro(erro);
  if (typeof erro !== 'string') {
    console.warn(`Formulário: ${detalheDoErro(erro)}`);
    registrarErro(erro, { onde: comDetalhe ? 'entrada' : 'formulario', mensagem: texto });
  }
  // Nas telas de entrada, o erro técnico vai junto, em letra pequena (para o print).
  const conteudo = comDetalhe && typeof erro !== 'string'
    ? [texto, h('small', { class: 'detalhe-erro' }, `Detalhe: ${detalheDoErro(erro)}`)]
    : texto;
  if (geral) {
    geral.replaceChildren(faixa(conteudo, 'perigo'));
    geral.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  } else {
    avisar(texto, 'erro');
  }
}

// Formulário com botão de salvar que trava enquanto envia.
//   validar(dados) → { campo: 'erro' } | {}
//   aoEnviar(dados) → Promise
export function formulario({
  filhos, textoBotao = 'Salvar', textoEnviando = 'Salvando…', validar = null, aoEnviar, somenteLeitura = false, extras = null, comDetalhe = false,
}) {
  const botao = h('button', { class: 'botao cheio', type: 'submit', disabled: somenteLeitura }, textoBotao);
  const form = h('form', { novalidate: true },
    filhos,
    h('div', { class: 'erro-geral', role: 'alert' }),
    h('div', { class: 'acoes' }, somenteLeitura ? null : botao, extras));

  if (somenteLeitura) {
    for (const el of form.querySelectorAll('input, select, textarea')) el.disabled = true;
  }

  // Quem corrige o campo não precisa continuar vendo o erro dele.
  const limparDoCampo = (evento) => {
    const bloco = evento.target.closest('[data-campo]');
    if (!bloco) return;
    const erro = bloco.querySelector('.erro-campo');
    if (erro?.textContent) {
      erro.textContent = '';
      for (const c of bloco.querySelectorAll('[aria-invalid]')) c.removeAttribute('aria-invalid');
    }
  };
  form.addEventListener('input', limparDoCampo);
  form.addEventListener('change', limparDoCampo);
  // Formulário mexido não é redesenhado quando chegam dados do servidor.
  const marcarAlterado = () => {
    form.dataset.alterado = '1';
  };
  form.addEventListener('input', marcarAlterado);
  form.addEventListener('change', marcarAlterado);

  form.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    if (botao.disabled) return;
    limparErros(form);
    const dados = lerDados(form);
    const erros = validar ? validar(dados) : {};
    if (mostrarErros(form, erros)) return;
    botao.disabled = true;
    const textoAntes = botao.textContent;
    botao.textContent = textoEnviando;
    try {
      await aoEnviar(dados, form);
    } catch (erro) {
      mostrarErroGeral(form, erro, { comDetalhe });
    } finally {
      if (botao.isConnected) {
        botao.disabled = false;
        botao.textContent = textoAntes;
      }
    }
  });
  return form;
}

// ---------------------------------------------------------------------
// Blocos
// ---------------------------------------------------------------------
export function faixa(texto, tipo = 'info', titulo = null) {
  const simbolo = tipo === 'ok' ? 'check' : tipo === 'info' ? null : 'alerta';
  return h('div', { class: `faixa ${tipo}` },
    simbolo && icone(simbolo, { tamanho: 22 }),
    h('div', null, titulo && h('strong', null, titulo), texto));
}

export function emblema(texto, tipo = '') {
  return h('span', { class: `emblema ${tipo}` }, texto);
}

export function vazio({ simbolo = 'caminhao', titulo, texto = null, acao = null }) {
  return h('div', { class: 'vazio' }, icone(simbolo, { tamanho: 48 }), h('h2', null, titulo), texto && h('p', null, texto), acao);
}

export function carregando(texto = 'Carregando…') {
  return h('div', { class: 'carregando', role: 'status' }, texto);
}

// Placas no meio de um texto ("MDT7J86 · Scania") viram o desenho da placa
// Mercosul (span.placa); o resto do texto continua igual.
const PLACA_NO_TEXTO = /\b[A-Z]{3}-?\d[A-Z0-9]\d{2}\b/g;
export function comPlaca(texto) {
  if (typeof texto !== 'string' || !PLACA_NO_TEXTO.test(texto)) return texto;
  PLACA_NO_TEXTO.lastIndex = 0;
  const partes = [];
  let desde = 0;
  for (const m of texto.matchAll(PLACA_NO_TEXTO)) {
    if (m.index > desde) partes.push(texto.slice(desde, m.index));
    partes.push(h('span', { class: 'placa' }, m[0]));
    desde = m.index + m[0].length;
  }
  if (desde < texto.length) partes.push(texto.slice(desde));
  return partes;
}

export function cartaoLink({ href, titulo, linhas = [], emblemas = [], simbolo = null, rotulo = null }) {
  return h('a', { class: 'cartao', href, 'aria-label': rotulo },
    simbolo && icone(simbolo, { tamanho: 28 }),
    h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, comPlaca(titulo)),
      linhas.filter(Boolean).map((l) => h('span', { class: 'cartao-linha' }, l)),
      emblemas.filter(Boolean).length > 0 && h('span', { class: 'cartao-emblemas' }, emblemas.filter(Boolean))),
    icone('seta', { classe: 'icone seta' }));
}

export function secao(titulo, conteudo, { contador = null, acao = null } = {}) {
  return h('section', { class: 'secao' },
    h('div', { class: 'secao-titulo' }, h('h2', null, titulo), contador !== null && h('span', { class: 'contador' }, contador), acao),
    conteudo);
}

// ---------------------------------------------------------------------
// Aviso rápido (some sozinho)
// ---------------------------------------------------------------------
export function avisar(texto, tipo = 'ok') {
  let area = document.querySelector('.avisos');
  if (!area) {
    area = h('div', { class: 'avisos', 'aria-live': 'polite' });
    document.body.append(area);
  }
  const aviso = h('div', { class: `aviso ${tipo}`, role: tipo === 'erro' ? 'alert' : 'status' }, texto);
  area.replaceChildren(aviso);
  setTimeout(() => aviso.remove(), tipo === 'erro' ? 6000 : 3500);
}

// ---------------------------------------------------------------------
// Diálogos
// ---------------------------------------------------------------------
// Abre um diálogo; `montar(fechar)` devolve o conteúdo. Resolve com o valor
// passado para fechar(valor) (ou null se cancelado).
export function dialogo(montar) {
  return new Promise((resolver) => {
    const d = h('dialog');
    let valor = null;
    const fechar = (v = null) => {
      valor = v;
      d.close();
    };
    d.addEventListener('close', () => {
      d.remove();
      resolver(valor);
    });
    d.append(h('div', { class: 'dialogo' }, montar(fechar)));
    document.body.append(d);
    d.showModal();
  });
}

export function confirmar({ titulo, texto, sim = 'Confirmar', nao = 'Cancelar', perigo = false }) {
  return dialogo((fechar) => [
    h('h2', null, titulo),
    texto && h('p', null, texto),
    h('div', { class: 'acoes' },
      h('button', { class: `botao cheio ${perigo ? 'perigo-cheio' : ''}`, type: 'button', onClick: () => fechar(true) }, sim),
      h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(false) }, nao)),
  ]).then((v) => v === true);
}
